import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { object, string, boolean } from 'yup';
import { Row, Col } from 'react-bootstrap';
import get from 'lodash/get';
import { Button } from '@storable/canaveral-components';
import styles from './styles.scss';
import { validateHexCode } from '../../../modules/theme/colors';
import formatInitialValues from '../../lib/formatInitialValues';
import FormFieldToggle from '../FormFieldToggle';
import FormSubmitButton from '../FormSubmitButton';
import withForm from '../../hocs/withForm';
import FacilityStylingForm from '../FacilityStylingForm';
import PrimaryFacilityImageForm from '../PrimaryFacilityImageForm';
import SocialMediaIconForm from '../SocialMediaIconForm';

const FACILIITY_LOGO_SECTION_NAME = 'logo';
const PRIMARY_IMAGE_SECTION_NAME = 'primary';

const propTypes = {
  activeSection: PropTypes.string,
  appliedMedia: PropTypes.shape({
    [FACILIITY_LOGO_SECTION_NAME]: PropTypes.shape({
      alt: PropTypes.string,
      title: PropTypes.string,
      mediumId: PropTypes.string,
    }),
    [PRIMARY_IMAGE_SECTION_NAME]: PropTypes.shape({
      alt: PropTypes.string,
      title: PropTypes.string,
      mediumId: PropTypes.string,
    }),
  }),
  listing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    primaryImageMediumId: PropTypes.string.isRequired,
    theme: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      colors: PropTypes.shape({
        primary: PropTypes.string,
        secondary: PropTypes.string,
        tertiary: PropTypes.string,
      }),
      logo: PropTypes.shape({
        alt: PropTypes.string,
        link: PropTypes.shape({
          href: PropTypes.string,
          target: PropTypes.string,
        }),
        path: PropTypes.string,
        title: PropTypes.string,
      }),
    }).isRequired,
    socialMedia: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      platforms: PropTypes.arrayOf(
        PropTypes.shape({
          enabled: PropTypes.bool.isRequired,
          link: PropTypes.string.isRequired,
          provider: PropTypes.string.isRequired,
        }),
      ),
    }).isRequired,
  }).isRequired,
  facility: PropTypes.shape().isRequired,
  submitting: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  logoEnabled: PropTypes.bool,
  socialMediaEnabled: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  dirty: PropTypes.bool.isRequired,
  setActiveSection: PropTypes.func.isRequired,
  setAppliedMedia: PropTypes.func.isRequired,
  setShowMediaGallery: PropTypes.func.isRequired,
  setDirty: PropTypes.func.isRequired,
  setLogoEnabled: PropTypes.func.isRequired,
  setSocialMediaEnabled: PropTypes.func.isRequired,
  site: PropTypes.shape({
    theme: PropTypes.shape({
      colors: PropTypes.shape({
        primary: PropTypes.string.isRequired,
        secondary: PropTypes.string.isRequired,
        tertiary: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    publicLogo: PropTypes.shape({
      alt: PropTypes.string,
      link: PropTypes.shape({
        href: PropTypes.string.isRequired,
      }),
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
    }).isRequired,
  }).isRequired,
  showMediaGallery: PropTypes.bool.isRequired,
  values: PropTypes.shape({
    theme: PropTypes.shape({
      colors: PropTypes.shape({
        primary: PropTypes.string.isRequired,
        secondary: PropTypes.string.isRequired,
        tertiary: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    enabled: PropTypes.bool.isRequired,
    logo: PropTypes.shape({
      alt: PropTypes.string,
      link: PropTypes.shape({
        href: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
      }),
      mediumId: PropTypes.string,
      path: PropTypes.string.isRequired,
      title: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

const defaultProps = {
  activeSection: null,
  appliedMedia: {},
  logoEnabled: false,
  socialMediaEnabled: false,
};

const initialValues = {
  primaryImageMediumId: '',
  primaryImage: {
    alt: '',
    title: '',
  },
  theme: {
    enabled: false,
    colors: {
      primary: '',
      secondary: '',
      tertiary: '',
    },
    logo: {
      alt: '',
      link: {
        href: '/',
        target: '_self',
      },
      mediumId: '',
      path: '',
      title: '',
    },
  },
  socialMediaPlatforms: [],
  socialMediaEnabled: false,
};

// Yup currently doesn't allow access to parent values inside its `when` fn
// https://github.com/jquense/yup/issues/289
// TODO: add required to fields only when enabled is true
//
// primary: string()
//   .when('theme.enabled', {
//     is: true,
//     then: string()
//       .test('is-primary-valid', 'Primary color is not valid', validateHexCode),
//   }),
// ...
const validationSchema = object().shape({
  theme: object().shape({
    enabled: boolean()
      .required(),
    colors: object().shape({
      primary: string()
        .test('is-primary-valid', 'Primary color is not valid', validateHexCode),
      secondary: string()
        .test('is-secondary-valid', 'Secondary color is not valid', validateHexCode),
      tertiary: string()
        .test('is-tertiary-valid', 'Tertiary color is not valid', validateHexCode),
    }),
    logo: object().shape({
      alt: string(),
      link: object().shape({
        href: string(),
        target: string(),
      }),
      mediumId: string(),
      path: string(),
      title: string(),
    }),
  }),
});

const FacilityEditForm = ({
  activeSection,
  appliedMedia,
  facility,
  listing,
  dirty,
  site,
  submitting,
  handleSubmit,
  handleReset,
  logoEnabled,
  socialMediaEnabled,
  values,
  setFieldValue,
  onDismiss,
  setActiveSection,
  setAppliedMedia,
  setLogoEnabled,
  setSocialMediaEnabled,
  setShowMediaGallery,
  showMediaGallery,
  ...rest
}) => {
  const [sameMedia, setSameMedia] = useState(false);
  const { theme: { enabled }, socialMediaEnabled: socialMediaEnabledForm } = values;
  const onImageReset = () => {
    setAppliedMedia(FACILIITY_LOGO_SECTION_NAME, null);
    setFieldValue(
      'theme.logo',
      {
        alt: site.theme.logo ? site.theme.logo.alt : '',
        mediumId: site.theme.logo ? site.theme.logo.mediumId : '',
        path: '',
        title: site.theme.logo ? site.theme.logo.title : '',
      },
      false,
    );
  };

  useEffect(() => {
    setFieldValue('theme.enabled', logoEnabled || enabled);
    setFieldValue('socialMediaEnabled', socialMediaEnabled || socialMediaEnabledForm);
  }, []);

  useEffect(() => {
    setLogoEnabled(enabled);
  }, [showMediaGallery, enabled]);

  useEffect(() => {
    setSocialMediaEnabled(socialMediaEnabledForm);
  }, [showMediaGallery, socialMediaEnabledForm]);

  useEffect(() => {
    if (listing && listing.theme.logo) {
      setFieldValue('theme.logo.mediumId', listing.theme.logo.mediumId);
    } else if (site && site.theme.logo) {
      setFieldValue('theme.logo.mediumId', site.theme.logo.mediumId);
    } else {
      setFieldValue('theme.logo.mediumId', '');
    }
  }, [listing]);

  useEffect(() => {
    if (appliedMedia && appliedMedia[FACILIITY_LOGO_SECTION_NAME]) {
      setFieldValue('theme.logo.mediumId', appliedMedia[FACILIITY_LOGO_SECTION_NAME].mediumId);
    }
  }, [appliedMedia]);

  useEffect(() => {
    if (values.theme.logo.mediumId === values.primaryImageMediumId) {
      setSameMedia(true);
      setFieldValue('theme.logo.alt', values.primaryImage.alt);
      setFieldValue('theme.logo.title', values.primaryImage.title);
    } else {
      setSameMedia(false);
    }
  }, [values.theme, values.primaryImageMediumId, values.primaryImage]);

  const themeSource = get(values, 'theme.enabled') ? values : site;
  const iconColor = get(themeSource, 'theme.colors.secondary');
  return (
    <form onSubmit={handleSubmit} onReset={handleReset} className={styles.container}>
      <div className={styles.formContainer}>
        <Row className={styles.primaryImageRow}>
          <Col xs={12}>
            <PrimaryFacilityImageForm
              activeSection={PRIMARY_IMAGE_SECTION_NAME}
              appliedMedia={appliedMedia}
              values={values}
              listing={listing}
              facility={facility}
              setActiveSection={setActiveSection}
              setAppliedMedia={setAppliedMedia}
              setFieldValue={setFieldValue}
              setShowMediaGallery={setShowMediaGallery}
              showMediaGallery={showMediaGallery}
              {...rest}
            />
          </Col>
        </Row>
        {!listing.linkOutUrl
          && (
            <>
              <Row>
                <Col xs={12}>
                  <FormFieldToggle
                    description="This will override site branding for this facility"
                    inline
                    label="Allow Custom Facility Branding"
                    type="checkbox"
                    name="theme.enabled"
                    required
                  />
                </Col>
              </Row>
              {get(values, 'theme.enabled') && (
              <FacilityStylingForm
                activeSection={FACILIITY_LOGO_SECTION_NAME}
                sameMedia={sameMedia}
                onImageReset={onImageReset}
                setActiveSection={setActiveSection}
                setAppliedMedia={setAppliedMedia}
                setShowMediaGallery={setShowMediaGallery}
                setFieldValue={setFieldValue}
                showMediaGallery={showMediaGallery}
                values={values}
                {...rest}
              />
              )}
              <Row className={styles.socialMediaToggle}>
                <Col xs={12}>
                  <FormFieldToggle
                    description="This will override the links set in site branding for this facility"
                    inline
                    label="Allow Facility Social Media Accounts"
                    type="checkbox"
                    name="socialMediaEnabled"
                    required
                  />
                </Col>
              </Row>
              {get(values, 'socialMediaEnabled') && (
              <SocialMediaIconForm
                limit={5}
                setFieldValue={setFieldValue}
                values={values}
                title="Facility Social Media Accounts"
                iconColor={iconColor}
              />
              )}
            </>
          )
        }
      </div>
      <div className={styles.footer}>
        <Button
          context="neutral"
          onClick={onDismiss}
          size="sm"
        >
          Close
        </Button>
        <FormSubmitButton
          disabled={submitting || !dirty}
          submitting={submitting}
          size="sm"
        />
      </div>
    </form>
  );
};

FacilityEditForm.propTypes = propTypes;
FacilityEditForm.defaultProps = defaultProps;

FacilityEditForm.fragments = {
  facility: gql`
    fragment FacilityEditFormFacility on Facility {
      id
      ...PrimaryImageFormFacility
    }

    ${PrimaryFacilityImageForm.fragments.facility}
  `,
  listing: gql`
    fragment FacilityEditFormListing on Listing {
      id
      ...PrimaryImageFormListing
      theme {
        enabled
        colors {
          primary
          secondary
          tertiary
        }
        logo {
          path
          mediumId
          alt
          title
        }
      }
      socialMedia {
        enabled
        platforms {
          enabled
          link
          provider
        }
      }
      linkOutUrl
    }

    ${PrimaryFacilityImageForm.fragments.listing}
  `,
  site: gql`
    fragment FacilityEditFormSite on Site {
      theme {
        colors {
          primary
          secondary
          tertiary
        }
        logo {
          mediumId
          alt
          title
        }
      }
     publicLogo {
       url
       alt
       title
       link {
         href
       }
     }
    }
  `,
};

export default withForm({
  enableReinitialize: true,
  mapPropsToValues: props => formatInitialValues(
    initialValues,
    props.site,
    props.listing,
    { socialMediaEnabled: props.socialMediaEnabled },
    props.listing && props.listing.socialMedia ? {
      ...props.listing,
      socialMediaEnabled: props.listing.socialMedia.enabled,
      socialMediaPlatforms: props.listing.socialMedia.platforms.reduce((platforms, platform) => ({
        ...platforms,
        [platform.provider]: {
          link: platform.link,
          enabled: platform.enabled,
        },
      }), {}),
    } : props.listing,
  ),
  validationSchema,
  handleSubmit: (updatedFacility, formikBag) => formikBag.props.onSubmit(updatedFacility),
})(FacilityEditForm);
